import { useCallback, useRef, useState } from 'react';
import { FormatListBulletedOutlined } from '@mui/icons-material';
import {
	ConfirmModal,
	FormActions,
	FormCard,
	FormSectionTitle,
	PageHeader,
	useConfirmModal,
	useToast,
} from '@practium/ui';
import type { FormPageMode } from '@practium/ui';
import type {
	AnalysesTab,
	AnalysisPageMode,
	SoilAnalysisFormData,
	PlantAnalysisFormData,
} from '../types/analyses.types';
import { SoilAnalysisForm } from '../components/Forms/SoilAnalysisForm/SoilAnalysisForm';
import type { SoilAnalysisFormRef } from '../components/Forms/SoilAnalysisForm/SoilAnalysisForm';
import { PlantAnalysisForm } from '../components/Forms/PlantAnalysisForm/PlantAnalysisForm';
import type { PlantAnalysisFormRef } from '../components/Forms/PlantAnalysisForm/PlantAnalysisForm';
import { useDeletePlantAnalysis, useDeleteSoilAnalysis } from '@practium/features/api';
import {
	ANALYSIS_TITLES,
	ANALYSIS_SUBTITLES,
	ANALYSIS_SECTION_TITLES,
} from '../constants/page.constants';
import styles from './AddAnalysisPage.module.css';
import type { OcrFoliarSample, OcrSoilSample } from '@practium/features/api';

export interface AddAnalysisPageProps {
	type: AnalysesTab;
	onBack: () => void;
	initialMode?: AnalysisPageMode;
	initialData?: SoilAnalysisFormData | PlantAnalysisFormData;
	ocrSample?: OcrSoilSample | OcrFoliarSample;
	ocrFileKey?: string;
	ocrFileName?: string;
	ocrSampleIndex?: number;
	ocrSampleTotal?: number;
	ocrWarning?: boolean;
	ocrWarningMessage?: string;
	onOcrSaved?: () => boolean;
	onOcrCancelled?: () => void;
	analysisId?: string;
	onDelete?: () => void;
	canEdit?: boolean;
}

export const AddAnalysisPage = ({
	type,
	onBack,
	initialMode = 'add',
	initialData,
	ocrSample,
	ocrFileKey,
	ocrFileName,
	ocrSampleIndex,
	ocrSampleTotal,
	ocrWarning = false,
	ocrWarningMessage = '',
	onOcrSaved,
	onOcrCancelled,
	analysisId,
	onDelete,
	canEdit = true,
}: AddAnalysisPageProps) => {
	const [currentMode, setCurrentMode] = useState<AnalysisPageMode>(initialMode);
	const [isLoading, setIsLoading] = useState(false);
	const [ocrBusy, setOcrBusy] = useState(false);
	const { mutateAsync: deleteSoilAnalysis, isPending: isDeletingSoil } = useDeleteSoilAnalysis();
	const { mutateAsync: deletePlantAnalysis, isPending: isDeletingPlant } = useDeletePlantAnalysis();

	const soilRef = useRef<SoilAnalysisFormRef>(null);
	const plantRef = useRef<PlantAnalysisFormRef>(null);

	const {
		confirm,
		isOpen: isConfirmOpen,
		options: confirmOptions,
		handleConfirm,
		handleCancel: handleConfirmCancel,
	} = useConfirmModal();

	const toast = useToast();

	const handleSubmit = useCallback(() => {
		const hasNextOcrSample = onOcrSaved?.() ?? false;
		if (hasNextOcrSample) return;
		onBack();
	}, [onBack, onOcrSaved]);

	const isFormDirty = useCallback(() => {
		if (type === 'soil') return soilRef.current?.isDirty?.() ?? false;
		return plantRef.current?.isDirty?.() ?? false;
	}, [type]);

	const handleCancel = useCallback(async () => {
		const revert = () => {
			soilRef.current?.discardImport?.();
			plantRef.current?.discardImport?.();
			if (currentMode === 'edit' && initialMode === 'view') {
				toast.success('Alterações descartadas.');
				setCurrentMode('view');
			} else {
				onOcrCancelled?.();
				toast.success('Análise cancelada com sucesso.');
				onBack();
			}
		};
		if (!isFormDirty()) {
			revert();
			return;
		}
		const confirmed = await confirm({
			title: 'Cancelar alterações',
			message: 'Deseja descartar as alterações não salvas?',
			confirmText: 'Descartar',
			cancelText: 'Continuar editando',
			variant: 'warning',
		});
		if (!confirmed) return;
		revert();
	}, [confirm, currentMode, initialMode, isFormDirty, onBack, onOcrCancelled, toast]);

	const handleDelete = useCallback(async () => {
		const confirmed = await confirm({
			title: 'Excluir análise',
			message: 'Tem certeza que deseja excluir esta análise? Esta ação não pode ser desfeita.',
			confirmText: 'Excluir',
			cancelText: 'Cancelar',
			variant: 'danger',
		});
		if (!confirmed) return;
		if (!analysisId) {
			toast.error('Análise não encontrada para exclusão.');
			return;
		}

		setIsLoading(true);
		try {
			if (type === 'soil') {
				await deleteSoilAnalysis(analysisId);
			} else {
				await deletePlantAnalysis(analysisId);
			}
			toast.success('Análise excluída com sucesso.');
			onDelete?.();
		} catch (err) {
			const msg = err instanceof Error ? err.message : 'Erro ao excluir análise';
			toast.error(msg);
		} finally {
			setIsLoading(false);
		}
	}, [analysisId, confirm, deletePlantAnalysis, deleteSoilAnalysis, onDelete, toast, type]);

	const handleEdit = useCallback(() => setCurrentMode('edit'), []);

	const handleSave = useCallback(() => {
		if (type === 'soil') soilRef.current?.submit();
		else plantRef.current?.submit();
	}, [type]);

	const formMode: FormPageMode = currentMode;
	const isView = currentMode === 'view';
	const title = ANALYSIS_TITLES[type][currentMode];
	const subtitle = ANALYSIS_SUBTITLES[currentMode][type];
	const sectionTitle = ANALYSIS_SECTION_TITLES[type];
	const isSubmitting = isLoading || isDeletingSoil || isDeletingPlant;

	const pageActions = (
		<div className={styles.pageActions}>
			<FormActions
				mode={formMode}
				onCancel={handleCancel}
				onSave={handleSave}
				isLoading={isSubmitting}
				saveDisabled={ocrBusy}
				onDelete={onDelete && analysisId ? handleDelete : undefined}
				onEdit={canEdit ? handleEdit : undefined}
			/>
		</div>
	);

	return (
		<div className={styles.page}>
			<div className={styles.mainContent}>
				<PageHeader title={title} subtitle={subtitle} onBack={onBack} />

				<FormCard
					icon={<FormatListBulletedOutlined />}
					title="Dados da Análise"
					className={styles.mainCard}
				>
					<div className={styles.formContent}>
						{!isView && <FormSectionTitle>{sectionTitle}</FormSectionTitle>}
						<div className={styles.fields}>
							{type === 'soil' && (
								<SoilAnalysisForm
									key={ocrFileKey}
									ref={soilRef}
									mode={currentMode}
									initialData={initialData as SoilAnalysisFormData | undefined}
									ocrSample={ocrSample as OcrSoilSample | undefined}
									ocrFileName={ocrFileName}
									ocrSampleIndex={ocrSampleIndex}
									ocrSampleTotal={ocrSampleTotal}
									ocrWarning={ocrWarning}
									ocrWarningMessage={ocrWarningMessage}
									analysisId={analysisId}
									onSubmit={handleSubmit}
									onLoadingChange={setIsLoading}
									onOcrBusyChange={setOcrBusy}
								/>
							)}
							{type === 'plant' && (
								<PlantAnalysisForm
									key={ocrFileKey}
									ref={plantRef}
									mode={currentMode}
									initialData={initialData as PlantAnalysisFormData | undefined}
									ocrSample={ocrSample as OcrFoliarSample | undefined}
									ocrFileName={ocrFileName}
									ocrSampleIndex={ocrSampleIndex}
									ocrSampleTotal={ocrSampleTotal}
									ocrWarning={ocrWarning}
									ocrWarningMessage={ocrWarningMessage}
									analysisId={analysisId}
									onSubmit={handleSubmit}
									onLoadingChange={setIsLoading}
									onOcrBusyChange={setOcrBusy}
								/>
							)}
						</div>
					</div>
				</FormCard>
				{pageActions}
			</div>

			<ConfirmModal
				isOpen={isConfirmOpen}
				title={confirmOptions.title}
				message={confirmOptions.message}
				confirmText={confirmOptions.confirmText}
				cancelText={confirmOptions.cancelText}
				variant={confirmOptions.variant}
				onConfirm={handleConfirm}
				onCancel={handleConfirmCancel}
			/>
		</div>
	);
};
